import type { SvgIconProps } from '@mui/material';
import type { ComponentType } from 'react';
import z from 'zod';

import { type IconName } from '@shared-components-web';
import LepCatIcon from '@src/icons/LepCatIcon';
import LepDogIcon from '@src/icons/LepDogIcon';
import { Sex } from '@src/lib/__generated/vetspire/sdk';
import { OFFENSIVE_LANGUAGE_MESSAGE, withOffensiveWords } from '@src/utils/offensive-words';

export enum PetSpeciesEnum {
	Dog = 'Canine',
	Cat = 'Feline',
	Other = 'Other',
}

export enum NeuteredEnum {
	True = 'true',
	False = 'false',
	Unknown = 'unknown',
}

export type SpeciesIllustration = ComponentType<SvgIconProps & { size?: string }>;

export interface PetSpecies {
	value: PetSpeciesEnum;
	icon: IconName;
	label: string;
	/** Large illustration (e.g. Lovet Essentials tab). Only Dog/Cat have one. */
	illustration?: SpeciesIllustration;
}

export const COMMON_SPECIES_NAME = {
	[PetSpeciesEnum.Dog]: 'Dog',
	[PetSpeciesEnum.Cat]: 'Cat',
};

export const SPAYED_OR_NEUTERED: Record<Sex, string> = {
	MALE: 'Neutered',
	FEMALE: 'Spayed',
	UNKNOWN: 'Spayed/Neutered',
};

export enum SexLabelEnum {
	MALE = 'Male',
	FEMALE = 'Female',
}

export const renderNeutered = (neutered: NeuteredEnum, sex: Sex) => {
	if (neutered === NeuteredEnum.Unknown) {
		return `${SPAYED_OR_NEUTERED[sex]} Unknown`;
	}
	return `${neutered === NeuteredEnum.False ? 'Not ' : ''}${SPAYED_OR_NEUTERED[sex]}`;
};

export const PET_SPECIES: PetSpecies[] = [
	{
		value: PetSpeciesEnum.Dog,
		label: COMMON_SPECIES_NAME[PetSpeciesEnum.Dog],
		icon: 'LVDog',
		illustration: LepDogIcon,
	},
	{
		value: PetSpeciesEnum.Cat,
		label: COMMON_SPECIES_NAME[PetSpeciesEnum.Cat],
		icon: 'LVCat',
		illustration: LepCatIcon,
	},
	{
		value: PetSpeciesEnum.Other,
		label: PetSpeciesEnum.Other,
		icon: 'LVOtherPet',
	},
];

export const SEX_OPTIONS: { value: Sex; label: string }[] = [
	{ value: 'MALE', label: 'Male' },
	{ value: 'FEMALE', label: 'Female' },
	{ value: 'UNKNOWN', label: 'Unknown' },
];

export const NEUTERED_OPTIONS: { value: string; label: string }[] = [
	{ value: NeuteredEnum.True, label: 'Yes' },
	{ value: NeuteredEnum.False, label: 'No' },
	{ value: NeuteredEnum.Unknown, label: 'Unknown' },
];

export const patientInformationForm = {
	species: {
		name: 'species',
		label: 'Species',
		schema: z.string().min(1, 'Species is required'),
	},
	petName: {
		name: 'petName',
		label: 'Pet Name',
		schema: z
			.string()
			.trim()
			.min(1, 'Pet name is required')
			.regex(/^[a-zÀ-ž\s\-~']+$/i, `Allowed characters are: A-Z ' - .`)
			.max(30, 'Max of 30 characters')
			.refine((value) => !withOffensiveWords(value), { message: OFFENSIVE_LANGUAGE_MESSAGE }),
	},
	sex: {
		name: 'sex',
		label: 'Sex',
		schema: z.string().min(1, 'Sex is required'),
	},
	neutered: {
		name: 'neutered',
		label: 'Spayed/Neutered',
		schema: z.string().min(1, 'Spayed/Neutered is required'),
	},
	birthDate: {
		name: 'birthDate',
		label: 'Pet Birthday',
		schema: z.date({ invalid_type_error: 'Pet Birthday is required' }).refine((date) => date < new Date(), {
			message: 'Please enter a valid birthday',
		}),
	},
	isEstimatedAge: {
		name: 'isEstimatedAge',
		label: 'This is an estimated birth date',
		schema: z.boolean(),
	},
	breed: {
		name: 'breed',
		label: 'Breed',
		schema: z.string().min(1, 'Breed is required'),
	},
	color: {
		name: 'color',
		label: 'Color',
		schema: z.string().min(1, 'Color is required'),
	},
};

export const patientInformationFormSchema = [
	{ name: patientInformationForm.species.name, validation: patientInformationForm.species.schema },
	{ name: patientInformationForm.petName.name, validation: patientInformationForm.petName.schema },
	{ name: patientInformationForm.sex.name, validation: patientInformationForm.sex.schema },
	{ name: patientInformationForm.neutered.name, validation: patientInformationForm.neutered.schema },
	{ name: patientInformationForm.birthDate.name, validation: patientInformationForm.birthDate.schema },
	{ name: patientInformationForm.isEstimatedAge.name, validation: patientInformationForm.isEstimatedAge.schema },
	{ name: patientInformationForm.breed.name, validation: patientInformationForm.breed.schema },
	{ name: patientInformationForm.color.name, validation: patientInformationForm.color.schema },
];

export const BREED_CANINE: string[] = [
	'Abruzzenhumk',
	'American Water Spaniel',
	'Affenpinscher',
	'Afghan Hound',
	'Akita Inu',
	'Alangu Mastiff',
	'Alano Espanol',
	'Alapaha Blue Blood Bulldog',
	'Alaskan Husky',
	'Alaskan Klee Kai',
	'Alaskan Malamute',
	'Appenzeller Sennenhund',
	'Alopekis',
	'Alpine Dachsbracke',
	'American Akita',
	'American Bulldog',
	'American Cocker Spaniel',
	'American Eskimo Dog',
	'American Foxhound',
	'American Indian Dog',
	'American Leopard Hound',
	'American Mastiff',
	'American Pit Bull Terrier',
	'American English Coonhound',
	'American Staffordshire Terrier',
	'Anatolian Shepherd Dog',
	'Anglo',
	'Appenzell Mountain Dog',
	'Arctic Husky',
	'Ariege Pointer',
	'Ariegeois',
	'Armant',
	'Artois Hound',
	'Australian Bulldog',
	'Australian Cattle Dog',
	'Australian Kelpie Terrier',
	'Australian Koolie',
	'Australian Shepherd',
	'Australian Silky Terrier',
	'Australian Stumpy Tail Cattle Dog',
	'Australian Terrier',
	'Austrian Black and Tan Hound',
	'Austrian Pinscher',
	'Azawakh',
	'Balkan Hound',
	'Bakharwal Dog',
	'Bandog',
	'Banjara Mastiff',
	'Barbet',
	'Basenji',
	'Basque Shepherd Dog',
	'Basset Artesien Normand',
	'Basset Bleu de Gascogne',
	'Basset Fauve de Bretagne',
	'Basset Griffon Vendeen',
	'Basset Hound',
	'Bavarian Mountain Hound',
	'Beagle',
	'Bearded Collie',
	'Bearded Tibetan Mastiff',
	'Beauceron',
	'Bedlington Terrier',
	'Belgian Griffon',
	'Belgian Shepherd Dog',
	'Bergamasco',
	'Berger Blanc Suisse',
	'Berger des Pyrenees',
	'Berger Picard',
	'Bernese Mountain Dog',
	'Bhotia',
	'Bichon Frise',
	'Biewer Terrier',
	'Bisben',
	'Black and Tan Coonhound',
	'Blackmouth Cur',
	'Black Norwegian Elkhound',
	'Bloodhound',
	'Blue Lacy',
	'Blue Paul Terrier',
	'Blue Picardy Spaniel',
	'Bluetick Coonhound',
	'Boerboel',
	'Bohemian Shepherd',
	'Bolognese',
	'Bolonka',
	'Borador',
	'Border Collie',
	'Border Terrier',
	'Borzoi',
	'Bosnian Coarse Haired Hound',
	'Bosnian Mountain Dog',
	'Boston Terrier',
	'Bouvier Bernois',
	'Bouvier des Ardennes',
	'Bouvier des Flandres',
	'Boxer',
	'Boykin Spaniel',
	'Bracco Italiano',
	"Braque d'Auvergne",
	'Braque du Bourbonnais',
	'Braque Francais',
	'Braque Saint-Germain',
	'Brazilian Mastiff',
	'Brazilian Terrier',
	'Briard',
	'Briquet Griffon Vendeen',
	'Brittany Spaniel',
	'Broholmer',
	'Brussels Griffon',
	'Bucovina Shepard',
	'Bull Terrier',
	'Bulldog',
	'Bulgarian Shepherd Dog',
	'Bully Kutta',
	'Bull Arab',
	'Burnese Mountain Dog',
	'Byelorussian Ovcharka',
	'Cabe\xE7udo Boiadeiro',
	'Ca de Bou',
	'Cairn Terrier',
	'Canaan Dog',
	'Canadian Eskimo Dog',
	'Canadian Pointer',
	'Cane Corso',
	'Canis Panther',
	'Cao da Serra de Aires',
	'Cao de Castro Laboreiro',
	'Cao de Fila de Sao Miguel',
	'Cao de Fila da Terceira',
	'Cao de Gado Transmontano',
	'Cardigan Welsh Corgi',
	'Carlin Pinscher',
	'Carolina Dog',
	'Carpatin',
	'Catahoula Bulldog',
	'Catahoula Cur',
	'Catalan Sheepdog',
	'Caucasian Ovcharka',
	'Cavalier King Charles Spaniel',
	'Cavachon',
	'Central Asian Shepherd',
	'Cesky Fousek',
	'Cesky Terrier',
	'Carpathian Shepherd Dog',
	'Chart Polski',
	'Chesapeake Bay Retriever',
	'Chihuahua',
	'Chipoo',
	'Chilean Fox Terrier',
	'Chinese Chongqing Dog',
	'Chinese Crested Dog',
	'Chinese Shar',
	'Chindo',
	'Chinook',
	'Chippiparai',
	'Chodsky pes',
	'Chow Chow',
	'Ciobanesc de Bucovina',
	'Circassian Orloff Wolfhound',
	"Cirneco dell'Etna",
	'Clumber Spaniel',
	'Cocker Spaniel',
	'Coonhound',
	'Collie',
	'Combai',
	'Cordoba Fighting Dog',
	'Corgi',
	'Coton de Tulear',
	'Croatian Mountain Dog',
	'Croatian Sheepdog',
	'Cur',
	'Curly Coated Retriever',
	'Cypro Kukur',
	'Czechoslovakian Wolfdog',
	'Dachshund',
	'Dalmatian',
	'Dandie Dinmont Terrier',
	'Danish Broholmer',
	'Danish/Swedish Farm Dog',
	'Deerhound',
	'Deutsch Drahthaar',
	'Deutsche Bracke',
	'Deutscher Wachtelhund',
	'Dhoki apso',
	'Dingo',
	'Dogo Argentino',
	'Dogo Cubano',
	'Dogo Guatemalteco',
	'Dogo Sardesco',
	'Dogue de Bordeaux',
	'Dogue de Majorque',
	'Dutch Partridge (Drentse Patrijshond)',
	'Drever',
	'Dr\xF3tsz\xF6r\xFC Magyar Vizsla',
	'Dunker',
	'Dutch Shepherd Dog',
	'Dutch Smoushond',
	'East European Shepherd',
	'East Siberian Laika',
	'Elo',
	'Elkhound',
	'English Bulldog',
	'English Cocker Spaniel',
	'English Coonhound',
	'English Foxhound',
	'English Mastiff',
	'English Pointer',
	'English Setter',
	'English Shepherd',
	'English Springer Spaniel',
	'English Toy Spaniel',
	'English Toy Terrier',
	'English White Terrier',
	'Entlebucher Mountain Dog/Sennenhund/Cattle Dog',
	'Epagneul Picard',
	'Eskimo Dog',
	'Estonian Hound',
	'Estrela Mountain Dog',
	'Eurasier',
	'Eurohound',
	'Fell Terrier',
	'Feist',
	'Field Spaniel',
	'Fila Brasileiro',
	'Finnish Hound',
	'Finnish Lapphund',
	'Finnish Spitz',
	'Formosan',
	'Foxhound',
	'Fox Terrier',
	'Francais Blanc et Noir',
	'Francais Blanc et Orange',
	'Francais Tricolore',
	'Franzuskaya Bolonka',
	'French Brittany',
	'French Bulldog',
	'French Spaniel',
	'Wirehaired Pointing Griffon',
	'Galgo Espanol',
	'Gawii',
	'German Coolie',
	'German Longhaired Pointer',
	'German Pinscher',
	'German Rough',
	'German Spaniel',
	'German Spitz',
	'Giant Schnauzer',
	'Glen of Imaal Terrier',
	'Goldendoodle',
	'Golden Mountain Dog',
	'Golden Retriever',
	'Gonczy Polski',
	'Gordon Setter',
	"Gos d'atura",
	'Grand Anglo',
	'Grand Basset Griffon Vendeen',
	'Grand Bleu de Gascogne',
	'Grand Gascon Saintongeois',
	'Grand Griffon Vendeen',
	'Gran Mastin de Borinquen',
	'Great Dane',
	'Great Pyrenees',
	'Greater Swiss Mountain Dog',
	'Greek Harehound',
	'Greek Sheepdog',
	'Greenland Dog',
	'Greyhound',
	'Griffon Bleu de Gascogne',
	'Griffon Bruxellois',
	'Griffon Fauve de Bretagne',
	'Griffon Nivernais',
	'Groenendael',
	'Guatemalan Bull Terrier',
	'Guejae Gae',
	'Gull Dong',
	'Gull Terr',
	'Hairless Khala',
	'Haldenst\xF8vare',
	'Hamiltonstovare',
	'Hanover Hound',
	'Harlequin Pinscher',
	'Harrier',
	'Havanese',
	'Hawaiian Poi Dog',
	'Hellenic Hound',
	'Hermes Bulldogge',
	'Himalayan Sheepdog',
	'Himalayan Mastiff',
	'Hokkaido',
	'Hollandse Herder',
	'Hortaya Borzaya',
	'Hovawart',
	'Hungarian Greyhound',
	'Hungarian Vizsla',
	'Huntaway',
	'Husky',
	'Hygenhund',
	'Icelandic Sheepdog',
	'Indian Spitz',
	'Indian Bull Terrier',
	'Irish Bull Terrier',
	'Inuit',
	'Irish Red and White Setter',
	'Irish Setter',
	'Irish Staffordshire Terrier',
	'Irish Terrier',
	'Irish Water Spaniel',
	'Irish Wolfhound',
	'Istarski Ostrodlaki Gonic',
	'Istrian Sheepdog',
	'Italian Greyhound',
	'Italian Volpino',
	'Jagdterrier',
	'J\xE4mthund',
	'Japanese Chin',
	'Japanese Mastiff (Tosa Inu)',
	'Japanese Spitz',
	'Japanese Terrier',
	'Jindo',
	'Jonangi',
	'Kaikadi',
	'Kai Ken',
	'Kangal Dog',
	'Kangaroo Dog',
	'Kanni',
	'Karafuto Ken',
	'Karelian Bear Dog',
	'Karelo',
	'Kars Dog',
	'Keeshond',
	'Kelb',
	'Kerry Beagle',
	'Kerry Blue Terrier',
	'King Shepherd',
	'Kintamani',
	'Kishu',
	'Kombai',
	'Komondor',
	'Kooikerhondje',
	'Koolie',
	'Korea Jindo Dog',
	'Korean Mastiff',
	'Korthals Griffon',
	'Kraski Ovcar',
	'Kritikos Ichnilatis',
	'Kromfohrlander',
	'Kuchi',
	'Kunming Dog',
	'Kuvasz',
	'Kyi Leo',
	'Labrador Mix',
	'Laekenois',
	'Lakeland Terrier',
	'Lancashire Heeler',
	'Landseer',
	'Lapponian herder',
	'Large Munsterlander',
	'Latvian Hound',
	'Leonberger',
	'Leopard Cur',
	'Lhasa Apso',
	'Lithuanian Hound',
	'Llewellin Setter',
	'Longdog',
	'Louisiana Catahoula Leopard Dog',
	'Lottatore Brindisino',
	'L\xF6wchen',
	'Lucas Terrier',
	'Lurcher',
	'Mackenzie River Husky',
	'Magyar Agar',
	'Majestic Tree Hound',
	'Belgian Malinois',
	'Maltalier',
	'Maltese',
	'Maltipoo',
	'Manchester Terrier',
	'Maremma Sheepdog',
	'Martin Mosa Mastiff',
	'Mastiff',
	'McNab Shepherd',
	'Meliteo Kinidio',
	'Mexican Hairless Dog (Xoloitzcuintli)',
	'Middle Asian Owtcharka',
	'Miniature American Shepherd',
	'Miniature Australian Shepherd',
	'Miniature Bull Terrier',
	'Miniature Fox Terrier',
	'Miniature Pinscher',
	'Miniature Schnauzer',
	'Miniature Siberian Husky',
	'Mioritic',
	'Moscow Guard dog',
	'Moscow Watchdog',
	'Mountain Burmese',
	'Mountain Cur',
	'Mongrel',
	'Mountain Feist',
	'Mucuchies',
	'Mudi',
	'Mudhol Hound',
	'Munsterlander',
	'Native American Indian Dog',
	'Neapolitan Mastiff',
	'Nebolish Mastiff',
	'Neilmut',
	'Nenets Herding Laika',
	'Newfoundland',
	'New Guinea Singing Dog',
	'Norfolk Terrier',
	'Norrbottenspets',
	'Northeasterly Hauling Laika',
	'Northern Inuit dog',
	'Norwegian Buhund (Sheepdog)',
	'Norwegian Elkhound',
	'Norwegian Lundehund',
	'Norwich Terrier',
	'Nova Scotia Duck Tolling Retriever',
	'Old Danish Pointer',
	'Old English Sheepdog',
	'Old English Bulldog',
	'Old English Terrier',
	'\xD6sterreichischer Kurzhaariger Pinscher',
	'Otterhound',
	'Otto',
	'Owczarek Podhalanski',
	'Pachon Navarro',
	'Papillon',
	'Parson Russell Terrier',
	'Pashmi',
	'Pastor Garafiano',
	'Patterdale Terrier',
	'Pekingese',
	'Pembroke Welsh Corgi',
	'Perdiguero de Burgos',
	'Perro Cimarron',
	'Perro de Pastor Mallorquin',
	'Perro de Presa Canario',
	'Perro de Presa Mallorquin',
	'Perro de Toro',
	'Peruvian Hairless Dog',
	'Peruvian Inca Orchid',
	'Petit Basset Griffon Vendeen',
	'Petit Berger',
	'Petit Bleu de Gascogne',
	'Petit Brabancon',
	'Petit Gascon Saintongeois',
	'Phalene',
	'Pharaoh Hound',
	'Phung San',
	'Picardy Shepherd',
	'Picardy Spaniel',
	'Doberman Pinscher',
	'Plott Hound',
	'Podenco Andaluz',
	'Podenco Canario',
	'Podenco Galego',
	'Podenco Ibicenco',
	'Pointer',
	'Poitevin',
	'Polish Scenthound',
	'Polish Greyhound',
	'Polish Sighthound',
	'Polish Hound',
	'Polish Lowland Sheepdog',
	'Polish Tatra Sheepdog',
	'Pomeranian',
	'Pont-Audemer Spaniel',
	'Miniature Poodle',
	'Poodle',
	'Porcelaine',
	'Portuguese Podengo',
	'Portuguese Pointer',
	'Portuguese Shepherd Dog',
	'Portuguese Water Dog',
	'Posavac Hound',
	'Prague Ratter',
	'Pudelpointer',
	'Pug',
	'Pug Mix',
	'Pugnaces Britanniae',
	'Puli',
	'Pumi',
	'Pungsan',
	'Pyrenean Mastiff',
	'Pyrenean Mountain Dog',
	'Pyrenean Shepherd',
	'Rafeiro do Alentejo',
	'Rajapalayam',
	'Rampur Greyhound',
	'Ratonero Bodeguero Andaluz',
	'Ratonero',
	'Rat Terrier',
	'Redbone Coonhound',
	'Red Setter',
	'Rhodesian Ridgeback',
	'Rottweiler',
	'Rough Collie',
	'Russian Black Terrier',
	'Russian Harlequin Hound',
	'Russian Hound',
	'Russian Setter',
	'Russian Spaniel',
	'Russian Toy Terrier',
	'Russian Tsvetnaya Bolonka',
	'Russo',
	'Russell Terrier',
	'Ryukyu Inu',
	'Saarlooswolfhond',
	'Sabueso espanol',
	'Sage Koochee',
	'Sakhalin Husky',
	'Saluki',
	'Samoyed',
	'Sanshu',
	'Santal Hound',
	'Sapsali',
	'\u0160arplaninac',
	'Schapendoes',
	'Schillerstovare',
	'Schipperke',
	'Schnauzer',
	'Schweizer Laufhund',
	'Schweizer Niederlaufhund',
	'Scottish Deerhound',
	'Scottish Terrier',
	'Sealyham Terrier',
	'Segugio Italiano',
	'Seppala Siberian Sleddog',
	'Serbian Hound',
	'Serbian Mountain Hound',
	'Serbian Tricolour Hound',
	'Shar Pei',
	'Shetland Sheepdog',
	'Shi',
	'Shiba Inu',
	'Shih Tzu',
	'Shikoku',
	'Shiloh Shepherd Dog',
	'Siberian Husky',
	'Silken Windhound',
	'Silky Terrier',
	'Sindh Mastiff',
	'Skye Terrier',
	'Sloughi',
	'Slovak Cuvac',
	'Slovakian Hound',
	'Slovensky Hrubosrsty Stavac',
	'Smalandsstovare',
	'Small Greek Domestic Dog',
	'Small Munsterlander',
	'Smithfield',
	'Smooth Collie',
	'Smooth Fox Terrier',
	'Snowfur Longsnout',
	'South Russian Ovtcharka',
	'Spanish Greyhound',
	'Spanish Mastiff',
	'Spanish Pointer',
	'Spanish Water Dog',
	'Spinone Italiano',
	'Spitz',
	'Springer Spaniel',
	'Stabyhoun',
	'Staffordshire Bull Terrier',
	'Standard Schnauzer',
	'Stephens Stock',
	'Styrian Coarse Haired Hound',
	'Sulimov dog',
	'Sussex Spaniel',
	'Swedish Elkhound',
	'Swedish Lapphund',
	'Swedish Vallhund',
	'Swiss Shorthaired Pinscher',
	'Tahltan Bear Dog',
	'Taigan',
	'Tainaker',
	'Ta\xEDno Dog',
	'Taiwan Dog',
	'Tamaskan dog',
	'Tasy',
	'Tatra Shepherd Dog',
	'Tenterfield Terrier',
	'Belgian Tervuren',
	'Thai Bangkaew Dog',
	'Thai Hairless Dog',
	'Thai Ridgeback',
	'Teddy Roosevelt Terrier',
	'Telomian',
	'Tibetan Kyi Apso',
	'Tibetan Lhasa Apso',
	'Tibetan Mastiff',
	'Tibetan Spaniel',
	'Tibetan Terrier',
	'Tornjak',
	'Toureg Sloughi',
	'Toy Bulldog',
	'Toy Fox Terrier',
	'Toy Manchester Terrier',
	'Toy Poodle',
	'Transylvanian Hound',
	'Treeing Cur',
	'Treeing Feist',
	'Treeing Tennessee Brindle',
	'Treeing Walker Coonhound',
	'Tsvetnaya Bolonka',
	'Turkish Kangal',
	'Tyrolean Hound',
	'German Shepherd',
	'Bichon Mix',
	'Terrier Mix',
	'Labrador Retriever',
	'Heading Dog',
	'Mountain Dog',
	'Bull Mastiff',
	'German Short Haired Pointer',
	'German Wire Hair Pointer',
	'Jack Russell Terrier',
	'Jack Russell Terrier Mix',
	'Whippet',
	'West Highland White Terrier',
	'Pitbull',
	'Mastiff Mix',
	'Sydney Silkie',
	'Staffordshire Mix',
	'Sheep Dog',
	'Saint Bernard',
	'Ridgeback',
	'Mix Breed',
	'Pomeranian Mix',
	'Retriever',
	'Bull Mastiff Mix',
	'Bearded Collie Mix',
	'Irish Setter MIx',
	'Springer Spaniel Mix',
	'Cavalier King Charles Spaniel Mix',
	'Poodle Mix',
	'Ridgeback Mix',
	'Wheaten Terrier',
	'Spaniel Mix',
	'Shetland Sheepdog Mix',
	'Spoodle',
	'Pig Dog',
	'Pointer Mix',
	'Cocker Spaniel Mix',
	'West Highland White Terrier Mix',
	'Maltese Mix',
	'Malamute',
	'Weimaraner',
	'Huntaway Mix',
	'Airedale Terrier',
	'English Bull Terrier',
	'Lagotto Romagnolo',
	'Flat Coat Retriever',
	'Boxer Mix',
	'Fox Terrier Mix',
	'Border Collie Mix',
	'Dalmatian Mix',
	'Pitbull Mix',
	'Yorkshire Terrier',
	'Griffon Mix',
	'Gray Fox',
	'Red Fox',
	'Labradoodle',
	'Vizsla',
	'Chihuahua Mix',
	'Akita',
	'Akita Mix',
	'Basenji Mix',
	'Beagle Mix',
	'Bloodhound Mix',
	'Briard Mix',
	'Bulldog Mix',
	'Louisiana Catahoula Leopard Dog Mix',
	'Japanese Chin Mix',
	'Chinese Crested Dog Mix',
	'Chow Chow Mix',
	'Cockapoo',
	'Collie Mix',
	'Airedale Terrier Mix',
	'Australian Shepherd Mix',
	'Australian Cattle Dog Mix',
	'American Eskimo Dog Mix',
	'Akbash',
	'Afghan Hound Mix',
	'Alaskan Malamute Mix',
	'Schnauzer Mix',
	'Pekingese Mix',
	'Shepherd Mix',
	'Standard Poodle',
	'Rottweiler Mix',
	'Retriever Mix',
	'Borzoi Mix',
	'Rat Terrier Mix',
	'Whippet Mix',
	'Shih Tzu Mix',
	'German Shepherd Mix',
	'Great Dane Mix',
	'Shiba Inu Mix',
	'Doberman Pinscher Mix',
	'Papillon Mix',
	'Great Pyrenees Mix',
	'Shar Pei Mix',
	'Hound',
	'Vizsla Mix',
	'Husky Mix',
	'Havanese Mix',
	'Lhasa Apso Mix',
	'Foxhound Mix',
	'Brussels Griffon Mix',
	'Schipperke Mix',
	'Terrier',
	'Golden Retriever Mix',
	'Weimaraner Mix',
	'Belgian Malinois Mix',
	'Puli Mix',
	'German Short Haired Pointer Mix',
	'Basset Hound Mix',
	'Welsh Terrier',
	'Coonhound Mix',
	'Portuguese Water Dog Mix',
	'Welsh Springer Spaniel',
	'Dingo Mix',
	'Bernese Mountain Dog Mix',
	'Akbash Mix',
	'Australian Kelpie Terrier Mix',
	'Greyhound Mix',
	'Keeshond Mix',
	'Wolf Mix',
	'Harrier Mix',
	'German Wire Hair Pointer Mix',
	'Newfoundland Mix',
	'Saluki Mix',
	'Wolfhound Mix',
	'Spitz Mix',
	'Elkhound Mix',
	'Old English Sheepdog Mix',
	'Samoyed Mix',
	'Saint Bernard Mix',
	'Bouvier des Flandres Mix',
	'Belgian Shepherd Dog Mix',
	'Dachshund Mix',
	'Jindo Mix',
	'Giant Schnauzer Mix',
	'Wolf',
	'Irish Soft Coated Wheaten Terrier',
	'Irish Wolfhound Mix',
	'Mi-ki',
	'Border Terrier Mix',
	'Boston Terrier Mix',
	'Bull Terrier Mix',
	'Brittany Spaniel Mix',
	'Cairn Terrier Mix',
	'Chesapeake Bay Retriever Mix',
	'Corgi Mix',
	'English Labrador',
	'Habibi Bear Dog',
	'Hound Mix',
	'English Staffordshire Terrier',
	'English Setter Mix',
	'Italian Greyhound Mix',
	'Formosan Mix',
	'Decker Hunting Terrier',
	'Flat Coat Retriever Mix',
	'Yorkshire Terrier Mix',
	'French Bulldog Mix',
	'Clumber Spaniel Mix',
	'Dogo Argentino Mix',
	'Plott Hound Mix',
	'Curly Coated Retriever Mix',
	'Canaan Dog Mix',
	'Ibizan Hound',
	'Bobtail Mix',
	'Miniature Dachshund',
	'Standard Dachshund',
	'Miniature Pinscher Mix',
	'Miniature Poodle Mix',
	'Koolie Mix',
	'Miniature Schnauzer Mix',
	'Norwich Terrier Mix',
	'Parson Russell Terrier Mix',
	'Wheaten Terrier Mix',
	'Scottish Terrier Mix',
	'Pharaoh Hound Mix',
	'Gordon Setter Mix',
	'Leonberger Mix',
	'Texas Heeler',
	'American Hairless Terrier',
	'Coyote',
	'American Bully',
	'Abyssinian Sand Terrier (African Hairless)',
	'Fennec Fox',
	'American Staghound',
	'Idaho Shag',
	'Mittel Spitz',
	'Belgian Tervuren Mix',
	'Blackmouth Cur Mix',
	'Chinese Foo Dog',
	'Hucul',
	'American Staffordshire Terrier Mix',
	'Hangin Tree Cowdog',
	'Finnish Spitz Mix',
	'Greater Swiss Mountain Dog Mix',
	'Silky Terrier Mix',
	'Scottish Deerhound Mix',
	'Cane Corso Mix',
	'Africanis',
	'Arubian Canucu',
];

export const BREED_FELINE: string[] = [
	'Aegean cat',
	'American Longhair',
	'Asian Semi-longhair',
	'Balinese',
	'Birman',
	'British Longhair',
	'Chantilly/Tiffany cat',
	'Himalayan',
	'Javanese',
	'Maine Coon',
	'Nebelung',
	'Norwegian Forest Cat',
	'Oriental Longhair',
	'Persian',
	'Ragdoll',
	'Siberian',
	'Turkish Van',
	'Turkish Angora',
	'Abyssinian',
	'American Shorthair',
	'Australian Mist',
	'Bombay',
	'Brazilian Shorthair',
	'British Shorthair',
	'Burmese',
	'Burmilla',
	'California Spangled Cat',
	'Chartreux',
	'Colorpoint Shorthair',
	'Egyptian Mau',
	'European Shorthair',
	'Exotic',
	'Havana Brown',
	'Ichabod',
	'Korat',
	'Ocicat',
	'Oriental Shorthair',
	'Russian Blue',
	'Russian',
	'Siamese',
	'Singapura',
	'Snowshoe',
	'Sokoke',
	'Somali',
	'Tonkinese',
	'Ukrainian Levkoy',
	'American Bobtail',
	'American Curl',
	'American Polydactyl',
	'American Wirehair',
	'Cornish Rex',
	'Cymric',
	'Devon Rex',
	'German Rex',
	'Japanese Bobtail',
	'LaPerm',
	'Manx',
	'Munchkin',
	'Ojos Azules',
	'Peterbald',
	'Pixie-Bob',
	'Selkirk Rex',
	'Scottish Fold',
	'Sphynx',
	'Domestic Short Hair (DSH)',
	'Domestic Long Hair (DLH)',
	'Unknown',
	'Domestic Medium Hair (DMH)',
	'Chinchilla Cat',
	'Bengal',
	'Siamese Mix',
	'British Blue',
	'Abyssinian Mix',
	'Angora Mix',
	'Bombay Mix',
	'Burmese Mix',
	'Scottish Fold Mix',
	'Savannah',
	'Maine Coon Mix',
	'Ragamuffin',
	'Snowshoe Mix',
	'Persian Mix',
	'Manx Mix',
	'Ragdoll Mix',
	'Russian Blue Mix',
	'Sphynx Mix',
	'Lynx',
	'Tonkinese Mix',
	'Balinese Mix',
	'Himalayan Mix',
	'Angora',
	'Arabian Mau',
	'Bengal Mix',
	'Egyptian Shorthair',
	'Highlander Cat',
	'Turkish Van Mix',
	'Pixie-Bob Mix',
	'Rex Mix',
	'Siberian Mix',
	'Somali Mix',
	'Bobcat Mix',
	'Asian Leopard',
	'Lion',
	'Cheetah',
	'Jungle Cat',
	'Serval',
	'Bambino',
	'Mokave Jag',
	'Napoleon Cat',
	'Siberian Tiger',
	'Khao Manee',
	'Sand Cat',
	'Jaguar',
	'Donskoy',
	'Cougar',
	'Indonesian Wildcat',
	'Tiger',
	'Panther',
	'Chausie',
	'Chinese Li Hua',
	'Kurilian Bobtail',
	'Mandalay',
	'Minskin',
	'Serengeti',
	'Toyger',
	'Cyprus',
	'Savannah Mix',
	'Chartreaux',
	'Domestic Dwarf',
	'LaPerm Mix',
	'Egyptian Mau Mix',
	'Ocelot',
	'Norwegian Forest Cat Mix',
	'Fishing Cat',
	'Thai Cat',
	'Foldex',
	'Lykoi',
	'Raas',
	'Sam Sawet',
	'Serrade Petit',
	'Suphalak',
	'York Chocolate',
	'Malayan',
];

export const COLORS_CANINE: string[] = [
	'Amber',
	'Appaloosa',
	'Apricot',
	'Apricot/White',
	'Auburn',
	'Bay',
	'Bay Roan',
	'Bay/White',
	'Beige',
	'Beige/Brown',
	'Beige/Cream',
	'Beige/White',
	'Black',
	'Black Baldy',
	'Black/Brown',
	'Black/Brown/White',
	'Black/White Face',
	'Black/Gray',
	'Black Spotted',
	'Black/Beige',
	'Black/Blonde',
	'Black/Cream',
	'Black/Gold',
	'Black/Grey',
	'Black/Grey/White',
	'Black/Mahogany',
	'Black/Red',
	'Black/Rust',
	'Black/Silver',
	'Black/Tan',
	'Black/White',
	'Black/White/Brown',
	'Black/White/Grey',
	'Black/White/Red',
	'Black/White/Tan',
	'Black/Yellow',
	'Blond',
	'Blonde',
	'Blonde/Brown',
	'Blonde/Golden',
	'Blonde/White',
	'Blue',
	'Blue Brindle',
	'Blue/Gold',
	'Blue/Grey',
	'Blue Fawn',
	'Blue Merle',
	'Blue Roan',
	'Blue/Tan',
	'Blue/White',
	'Brindle',
	'Bronze',
	'Brown',
	'Brown/Grey',
	'Brown/Black',
	'Brown/blck',
	'Brown/blk',
	'Brown/Grey/Black Stripe',
	'Brown/Liver',
	'Brown/Red',
	'Brown/Tan',
	'Brown/Tan/Black',
	'Brown/White',
	'Brown/White/Tan',
	'Brown/Yellow/Black',
	'Brown/Yellow/White',
	'Buckskin',
	'Buff',
	'Calico',
	'Caramel',
	'Caramel/White',
	'Carmel',
	'Champagne',
	'Charcoal',
	'Charcoal/Black',
	'Chestnut',
	'Chestnut/Black/White',
	'Chocolate',
	'Chocolate/Charcoal',
	'Chocolate Dapple',
	'Chocolate/White',
	'Cinnamon',
	'Copper',
	'Copper Brown',
	'Cream',
	'Cream/Apricot',
	'Cream/Black',
	'Cream/Brown',
	'Cream/Caramel',
	'Cream/Fawn',
	'Cream/Tan',
	'Cream/White',
	'Cremello',
	'Dark',
	'Dark Bay',
	'Dark Brown',
	'Dark Green',
	'Dun',
	'Fawn',
	'Fawn/Black',
	'Fawn/Red',
	'Fawn/White',
	'Gold',
	'Gold/Black',
	'Gold/Brown',
	'Gold/White',
	'Golden',
	'Golden Amber',
	'Green',
	'Grey',
	'Grey Baldy',
	'Grey/Black',
	'Grey/Black/Brown',
	'Grey/Black/White',
	'Grey/Brown',
	'Grey/Cream',
	'Grey/Gold',
	'Grey/Liver',
	'Grey/Rust',
	'Grey/Tan',
	'Grey/White',
	'Grulla',
	'Harlequin',
	'Honey',
	'Ivory',
	'Leopard',
	'Light',
	'Light Blue',
	'Light Brown',
	'Light Gold',
	'Light Tan',
	'Lilac',
	'Lineback Dun',
	'Liver',
	'Liver/Brown',
	'Liver/White',
	'Liver/Wht',
	'Mahogany',
	'Maroon',
	'Mottled',
	'Multi',
	'Multi Color',
	'Orange',
	'Orange/Black',
	'Orange/Cream',
	'Orange/White',
	'Overo',
	'Palomino',
	'Palomino/White',
	'Parti',
	'Peach',
	'Pink',
	'Red',
	'Red Baldy',
	'Red Dun',
	'Red Merle',
	'Red Roan',
	'Red/Black',
	'Red/Brown',
	'Red/Fawn',
	'Red/Gold',
	'Red/Rust',
	'Red/Tan',
	'Red/White',
	'Roan',
	'Ruby',
	'Russet',
	'Rust',
	'Rust/Black',
	'Rust/Tan',
	'Rust/White',
	'Sable',
	'Sable/White',
	'Salt/Pepper',
	'Sandy',
	'Seal',
	'Seal/White',
	'Sesame',
	'Silver',
	'Silver Dapple',
	'Silver/Black',
	'Silver/Brown',
	'Silver/Grey',
	'Silver/Red',
	'Silver/Tan',
	'Smoke',
	'Sorrel',
	'Sorrel/White',
	'Spotted',
	'Sulden',
	'Tan',
	'Tan/Black',
	'Tan/Blue',
	'Tan/Brown',
	'Tan/Cream',
	'Tan/Grey',
	'Tan/White',
	'Tan/White/Rust',
	'Tawny',
	'Ticked',
	'Tiger Stripe',
	'Tobiano',
	'Tri-Color',
	'Unknown Color',
	'Various',
	'Wheat',
	'Wheat/Black',
	'Wheat/Brown',
	'White',
	'White Spotted',
	'White/Black',
	'White/Black/Brown',
	'White/Brown',
	'White/Copper/Black',
	'White/Cream',
	'White/Fawn',
	'White/Gold',
	'White/Grey',
	'White/Liver',
	'White/Orange',
	'White/Red',
	'White/Silver/Black',
	'White/Spot',
	'White/Tan',
	'White/Tan/Black',
	'Yellow',
	'Yellow/Red',
	'Yellow/White',
];
export const COLORS_FELINE: string[] = [
	'Amber',
	'Apricot',
	'Apricot Tabby',
	'Apricot/White',
	'Beige',
	'Beige/Black',
	'Black',
	'Black/Brown',
	'Black Brow',
	'Black Smoked',
	'Black Spotted',
	'Black Tabby',
	'Black Torti',
	'Black/Gold',
	'Black/Cream',
	'Black/Grey',
	'Black/Silver',
	'Black/Tan',
	'Black/Tan/Brown',
	'Black/White',
	'Black/White/Taupe',
	'Blonde',
	'Blue',
	'Blue Cream',
	'Blue Cream Calico',
	'Blue Cream Tabby',
	'Blue Point',
	'Blue Tabby',
	'Blue/White',
	'Brown',
	'Brown/Gray',
	'Brown/White',
	'Brown Mackerel',
	'Brown Point',
	'Brown Spot',
	'Brown Tabby',
	'Brown/Beige',
	'Brown/Black',
	'Brown/Cream',
	'Brown/Grey',
	'Brown/Grey/Black Stripe',
	'Brown/Orange',
	'Brown/Tan Tabby',
	'Brown/White Tabby',
	'Buff',
	'Buff Tabby',
	'Calico',
	'Carmel',
	'Carmel/White',
	'Champagne',
	'Charcoal',
	'Charcoal/White',
	'Chestnut',
	'Chocolate Point',
	'Chocolate/Cream',
	'Cinnamon/Silver',
	'Cream',
	'Cream/Brown',
	'Cream/Grey',
	'Dark Grey',
	'Dark Tabby',
	'Dilute Calico',
	'Dilute Tabby',
	'Dilute Torti',
	'Ebony',
	'Fawn',
	'Field Point',
	'Flame',
	'Flame Point',
	'Gold',
	'Golden',
	'Grey',
	'Grey Point',
	'Grey Tabby',
	'Grey Tiger',
	'Grey Torti',
	'Grey White',
	'Grey/Black',
	'Grey/Black/White',
	'Grey/Brown',
	'Grey/Brown/Black',
	'Grey/Brown/White',
	'Grey/Cream',
	'Grey/Orange',
	'Grey/Silver Tabby',
	'Grey/Tan',
	'Grey/Tan/Black',
	'Grey/White',
	'Hairless',
	'Heather Tabby',
	'Leopard',
	'Light Blue',
	'Light Calico',
	'Lilac',
	'Lilac Point',
	'Lynx Point',
	'Lynx/Spot',
	'Mackeral/White',
	'Mixed',
	'Mixed Grey',
	'Multi',
	'Multi Color',
	'Muted Calico',
	'Natural Mink',
	'Orange',
	'Orange Tabby',
	'Orange Tiger',
	'Orange/Brown/Black',
	'Orange/Grey',
	'Orange/White',
	'Parti',
	'Pastel Calico',
	'Pastel Torti',
	'Platinum',
	'Platinum Point',
	'Red',
	'Red Tabby',
	'Red/White',
	'Reddish',
	'Rose Point',
	'Ruddy',
	'Sable',
	'Seal Lynx/Mittens',
	'Seal Point',
	'Sealpoint',
	'Shaded',
	'Silver',
	'Silver Patch Mark',
	'Silver Tabby',
	'Silver/Black',
	'Silver/White',
	'Smoke',
	'Sorrel',
	'Spotted',
	'Striped',
	'Tan',
	'Tan/Black',
	'Tan/Blk.',
	'Tan/Grey',
	'Tan/White',
	'Tiger',
	'Tiger Gray',
	'Tiger Grey',
	'Tiger Stripe',
	'Tortoise Shell',
	'Tortoiseshell',
	'Tri-color',
	'Tuxedo',
	'Unknown Color',
	'Various',
	'White',
	'White Spotted',
	'White Tabby',
	'White/Grey',
	'White/Beige',
	'White/Black',
	'White/Black/Brown',
	'White/Blue',
	'White/Brown',
	'White/BrownTabby',
	'White/Ginger',
	'White/Orange',
	'White/Tan',
	'Yellow',
	'Yellow Tabby',
	'Yellow/Grey/White',
];

export const BREED_OPTIONS: Record<string, string[]> = {
	[PetSpeciesEnum.Dog]: BREED_CANINE,
	[PetSpeciesEnum.Cat]: BREED_FELINE,
};
export const COLOR_OPTIONS: Record<string, string[]> = {
	[PetSpeciesEnum.Dog]: COLORS_CANINE,
	[PetSpeciesEnum.Cat]: COLORS_FELINE,
};

export const MICROCHIPPED_OPTIONS: { value: string; label: string }[] = [
	{ value: 'true', label: 'Yes' },
	{ value: 'false', label: 'No' },
	{ value: 'null', label: 'Unknown' },
];

/**
 * Formats a Date as `YYYY-MM-DD` using the browser's LOCAL calendar components.
 *
 * Do NOT use `date.toISOString().substring(0, 10)` for date-only fields — that
 * converts to UTC first and rolls the day backwards in timezones east of UTC
 * (e.g. IST, AEST — commonly used on BrowserStack Windows VMs), causing the
 * "birthday saved a day older" bug.
 */
export function formatDateOnly(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

/**
 * Normalize species to the canonical `PetSpeciesEnum` value ('Canine' | 'Feline' | 'Other').
 *
 * Use at every boundary — API reads AND mutation writes — so the rest of the app
 * only ever deals with a single title-case representation. `BREED_OPTIONS`,
 * `COLOR_OPTIONS`, and species icons are all keyed by this value.
 */
export function resolveSpeciesFormatting(species: string | null | undefined): string {
	const value = (species ?? '').trim();
	const lower = value.toLowerCase();
	if (lower === PetSpeciesEnum.Dog.toLowerCase()) return PetSpeciesEnum.Dog;
	if (lower === PetSpeciesEnum.Cat.toLowerCase()) return PetSpeciesEnum.Cat;
	if (lower === PetSpeciesEnum.Other.toLowerCase()) return PetSpeciesEnum.Other;
	return value;
}
